/**
 * Resting size of the docked chat interface. The dock is the global
 * entry point rendered in the host's bottom-right corner; `expanded`
 * shows the suggestion card, `minimized` collapses it to a bar.
 */
export const DOCK_STATE = {
  MINIMIZED: 'minimized',
  EXPANDED: 'expanded',
} as const;

export type DockState = (typeof DOCK_STATE)[keyof typeof DOCK_STATE];

/**
 * UI Redux state for `@brightdrop/ai-sidekick`. Owns panel
 * open/close state, dock size, entry-card chip selection, and the
 * input-bar focus token. Chat-domain state (messages, streaming, tool
 * activity, feedback, trace id, errors) lives in the sibling slice
 * exported from `./aiSidekickChatSlice.ts`.
 */
export type AISidekickUiState = {
  /** Whether the Sidekick drawer is currently open. */
  isPanelOpen: boolean;
  /**
   * Resting size of the docked entry point. Independent of
   * `isPanelOpen` in storage, but the dock never renders while the
   * drawer is open — the two surfaces occupy the same screen edge, so
   * opening the panel forces this back to `minimized`.
   */
  dockState: DockState;
  /**
   * Id of the suggestion chip most recently clicked in the entry
   * card. Drives the chip's "Selected" visual state. Cleared when
   * the panel closes, toggles from open to closed, or `resetUiState`
   * is dispatched.
   */
  selectedChipId: string | null;
  /**
   * Monotonically incremented counter that signals the input bar
   * to take focus. Components subscribe to this token via
   * `selectFocusToken` and call `inputRef.current?.focus()` whenever
   * the value changes. Avoids cross-boundary `useImperativeHandle`
   * plumbing — every increment is a fresh focus request.
   */
  focusToken: number;
  /**
   * Whether the in-chat "Start a new chat?" confirmation prompt is
   * currently visible. Driven by the header edit-icon click and the
   * prompt's own dismiss/confirm/auto-dismiss paths. Cleared when the
   * panel closes or `resetUiState` is dispatched.
   */
  isResetConfirmOpen: boolean;
  /**
   * Whether the reset confirmation prompt is mid-confirmation (the
   * "Continue" path between the click and the reset dispatch). Set to
   * `true` by `beginResetConfirm` and cleared by `closeResetConfirm`.
   * Lifted out of the prompt's local state because the chat shell's
   * auto-dismiss-on-typing behavior must NOT dismiss the prompt while
   * a reset is in flight — typing during the loading window would
   * otherwise unmount the prompt and cancel the timer before the
   * reset lands.
   */
  isResetConfirmLoading: boolean;
};

/**
 * Minimum shape the consumer's `RootState` must satisfy for this
 * package's selectors. Selectors are typed against this structural
 * shape so the package stays decoupled from any single app's store.
 */
export type StateWithSidekick = {
  aiSidekick: AISidekickUiState;
};
